/**
 * «Mi cuenta»: datos, contraseña, segundo factor y sesiones activas (docs/FASE-7.md §5 y §7).
 *
 * Es la primera interfaz que existe para el perfil de un usuario: hasta esta fase su nombre solo lo
 * cambiaba un operador, la contraseña solo se cambiaba por el flujo de olvido, y el segundo factor
 * no se podía activar de ninguna manera.
 */
import { useAuth, useAuthorizedFetch } from "@continnua/auth-web/react";
import type { MeResponse, MySession, MyTotpStatus } from "@continnua/contracts";
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";

import { api } from "../api";

function mensajeDe(err: unknown, porDefecto: string): string {
  return err instanceof Error && err.message.length > 0 ? err.message : porDefecto;
}

function Datos({ perfil, onChanged }: { perfil: MeResponse; onChanged: (perfil: MeResponse) => void }): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const [fullName, setFullName] = useState(perfil.fullName);
  const [locale, setLocale] = useState(perfil.locale);
  const [estado, setEstado] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function guardar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setGuardando(true);
    setEstado(null);
    try {
      onChanged(await api.updateMe(authorizedFetch, { fullName, locale }));
      setEstado("Guardado.");
    } catch (err) {
      setEstado(mensajeDe(err, "No se pudo guardar."));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <fieldset>
      <legend>Tus datos</legend>
      <form onSubmit={(e) => void guardar(e)}>
        <p className="muted">{perfil.email}</p>
        <label>
          <span>Nombre</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} required maxLength={200} />
        </label>
        <label>
          <span>Idioma</span>
          <input value={locale} onChange={(e) => setLocale(e.target.value)} required maxLength={35} placeholder="es-PE" />
        </label>
        <button type="submit" className="primary" disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </button>
        {estado !== null && <p className="muted">{estado}</p>}
      </form>
    </fieldset>
  );
}

function Contrasena(): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [estado, setEstado] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function cambiar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setGuardando(true);
    setEstado(null);
    try {
      await api.changePassword(authorizedFetch, actual, nueva);
      setActual("");
      setNueva("");
      setEstado("Contraseña cambiada. Las demás sesiones se cerraron.");
    } catch (err) {
      setEstado(mensajeDe(err, "No se pudo cambiar la contraseña."));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <fieldset>
      <legend>Contraseña</legend>
      <form onSubmit={(e) => void cambiar(e)}>
        <label>
          <span>Contraseña actual</span>
          <input type="password" value={actual} onChange={(e) => setActual(e.target.value)} required autoComplete="current-password" />
        </label>
        <label>
          <span>Contraseña nueva (mínimo 10 caracteres)</span>
          <input type="password" value={nueva} onChange={(e) => setNueva(e.target.value)} required minLength={10} autoComplete="new-password" />
        </label>
        <p className="muted">Al cambiarla se cerrarán tus otras sesiones, pero no esta.</p>
        <button type="submit" className="primary" disabled={guardando}>
          {guardando ? "Cambiando…" : "Cambiar contraseña"}
        </button>
        {estado !== null && <p className="muted">{estado}</p>}
      </form>
    </fieldset>
  );
}

function SegundoFactor(): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const [status, setStatus] = useState<MyTotpStatus | null>(null);
  const [secreto, setSecreto] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [password, setPassword] = useState("");
  const [estado, setEstado] = useState<string | null>(null);

  const recargar = useCallback(() => {
    api
      .totpStatus(authorizedFetch)
      .then(setStatus)
      .catch((err: unknown) => setEstado(mensajeDe(err, "No se pudo leer el estado del segundo factor.")));
  }, [authorizedFetch]);

  useEffect(recargar, [recargar]);

  async function iniciar(): Promise<void> {
    setEstado(null);
    try {
      const respuesta = await api.totpSetup(authorizedFetch);
      setSecreto(respuesta.secret);
      setOtpauth(respuesta.otpauthUrl);
      recargar();
    } catch (err) {
      setEstado(mensajeDe(err, "No se pudo iniciar la configuración."));
    }
  }

  async function confirmar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setEstado(null);
    try {
      setStatus(await api.totpVerify(authorizedFetch, codigo));
      setSecreto(null);
      setOtpauth(null);
      setCodigo("");
      setEstado("Segundo factor activado. Te lo pediremos al iniciar sesión.");
    } catch (err) {
      setEstado(mensajeDe(err, "El código no es válido."));
    }
  }

  async function desactivar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setEstado(null);
    try {
      setStatus(await api.totpDisable(authorizedFetch, password));
      setPassword("");
      setEstado("Segundo factor desactivado.");
    } catch (err) {
      setEstado(mensajeDe(err, "No se pudo desactivar."));
    }
  }

  if (status === null) return <fieldset><legend>Segundo factor</legend><p className="muted">Cargando…</p></fieldset>;

  return (
    <fieldset>
      <legend>Segundo factor (TOTP)</legend>

      {status.enabled ? (
        <form onSubmit={(e) => void desactivar(e)}>
          <p className="muted">Está activo. Para desactivarlo hay que confirmar con tu contraseña.</p>
          <label>
            <span>Contraseña</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </label>
          <button type="submit" className="danger">
            Desactivar
          </button>
        </form>
      ) : secreto !== null ? (
        <form onSubmit={(e) => void confirmar(e)}>
          <p className="muted">
            Añade esta cuenta a tu aplicación de códigos y escribe el código de seis dígitos que muestre. La clave se
            guarda desactivada hasta que la confirmes.
          </p>
          {otpauth !== null && <p className="muted" style={{ wordBreak: "break-all" }}>{otpauth}</p>}
          <p>
            <code className="secreto">{secreto}</code>
          </p>
          <label>
            <span>Código</span>
            <input value={codigo} onChange={(e) => setCodigo(e.target.value)} required inputMode="numeric" maxLength={8} autoComplete="one-time-code" />
          </label>
          <button type="submit" className="primary">
            Confirmar
          </button>
        </form>
      ) : (
        <>
          <p className="muted">
            {status.pending
              ? "Hay una configuración a medias. Empieza de nuevo para obtener una clave."
              : "No está activo. Añade un segundo factor para proteger tu cuenta."}
          </p>
          <button type="button" onClick={() => void iniciar()}>
            {status.pending ? "Empezar de nuevo" : "Activar"}
          </button>
        </>
      )}

      {estado !== null && <p className="muted">{estado}</p>}
    </fieldset>
  );
}

function Sesiones(): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const { signOut } = useAuth();
  const [sesiones, setSesiones] = useState<MySession[] | null>(null);
  const [estado, setEstado] = useState<string | null>(null);

  const recargar = useCallback(() => {
    api
      .sessions(authorizedFetch)
      .then((respuesta) => setSesiones(respuesta.sessions))
      .catch((err: unknown) => setEstado(mensajeDe(err, "No se pudieron cargar tus sesiones.")));
  }, [authorizedFetch]);

  useEffect(recargar, [recargar]);

  async function cerrar(sesion: MySession): Promise<void> {
    setEstado(null);
    try {
      await api.revokeSession(authorizedFetch, sesion.id);
      // Cerrar la propia sesión deja los tokens en memoria sin respaldo: lo coherente es salir.
      if (sesion.current) {
        signOut();
        return;
      }
      recargar();
    } catch (err) {
      setEstado(mensajeDe(err, "No se pudo cerrar la sesión."));
    }
  }

  return (
    <fieldset>
      <legend>Sesiones activas</legend>
      {sesiones === null ? (
        <p className="muted">Cargando…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">Dispositivo</th>
              <th scope="col">Último uso</th>
              <th scope="col">IP</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {sesiones.map((sesion) => (
              <tr key={sesion.id}>
                <td>
                  {sesion.userAgent ?? "Desconocido"}
                  {sesion.current && <span className="muted"> · esta sesión</span>}
                </td>
                <td>{new Date(sesion.lastSeenAt).toLocaleString("es-PE")}</td>
                <td>{sesion.ip ?? "—"}</td>
                <td>
                  <button type="button" className="danger" onClick={() => void cerrar(sesion)}>
                    Cerrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {estado !== null && <p className="muted">{estado}</p>}
    </fieldset>
  );
}

export function MiCuenta(): ReactElement {
  const authorizedFetch = useAuthorizedFetch();
  const [perfil, setPerfil] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .me(authorizedFetch)
      .then(setPerfil)
      .catch((err: unknown) => setError(mensajeDe(err, "No se pudo cargar tu perfil.")));
  }, [authorizedFetch]);

  if (error !== null) return <p className="error">{error}</p>;
  if (perfil === null) return <p className="muted">Cargando tu cuenta…</p>;

  return (
    <>
      <Datos perfil={perfil} onChanged={setPerfil} />
      <Contrasena />
      <SegundoFactor />
      <Sesiones />
    </>
  );
}
